import React from "react";

import type { ZohoDatasetReadDTO } from "../data/zohoRecordsApi";
import { CoButton } from "./Button";
import { RefreshIcon } from "./icons";
import { cn } from "./cn";

/**
 * How old the rows on this screen are, and a way to make them current.
 *
 * Every verify-setup screen reviews a stored copy of the client's books rather
 * than the books themselves, which is the right trade — seven live calls in
 * front of a CA is seven ways to fail. The cost is that the copy has an age,
 * and a screen that never says so invites somebody to act on a three-day-old
 * chart of accounts believing it is today's.
 *
 * Beyond a day it turns amber. Not an error: a chart of accounts does not go
 * stale on a schedule, and most days nothing has changed. It is the nudge to
 * press Refresh before editing, no more than that.
 */

/** After this, the age is worth looking at rather than just recording. */
const STALE_AFTER_HOURS = 24;

/**
 * MySQL hands back "2026-07-31 11:55:52" with no zone on it, and the app
 * writes in UTC. `new Date` on that string reads it as local time — five and a
 * half hours out here, and wrong in the flattering direction.
 */
function parseUtc(value: string): Date | null {
  const parsed = new Date(`${value.replace(" ", "T")}Z`);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * "3 days ago" rather than a timestamp: the question this line answers is
 * whether the copy is old enough to bother re-reading, and a date makes the
 * reader do that arithmetic themselves. The exact stamp is a hover away.
 */
function agoLabel(then: Date, now: Date): string {
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.round(hours / 24);

  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function DatasetFreshness({
  readback,
  busy,
  onRefresh,
  className,
}: {
  /** Undefined while the screen is still loading; null if we never asked. */
  readback: ZohoDatasetReadDTO | null | undefined;
  busy: boolean;
  onRefresh: () => void;
  className?: string;
}) {
  const failed = readback?.status === "failed";
  const fetchedAt = readback?.fetched_at ?? null;
  const at = fetchedAt === null ? null : parseUtc(fetchedAt);
  const stale = at !== null && Date.now() - at.getTime() > STALE_AFTER_HOURS * 3600 * 1000;

  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-x-3 gap-y-1", className)}>
      <span
        // The full stamp for anyone who needs to reason about a specific time,
        // in their own timezone rather than the server's.
        title={at === null ? undefined : at.toLocaleString()}
        className={cn(
          "text-xs",
          failed ? "text-amber-700 dark:text-amber-400" : stale ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
        )}
      >
        {busy
          ? "Reading from Zoho…"
          : failed
            ? "Last read failed"
            : at === null
              ? "Not read yet"
              : `Read ${agoLabel(at, new Date())}`}
      </span>

      <CoButton variant="outline" onClick={onRefresh} disabled={busy} className="h-7 px-2 text-xs">
        <RefreshIcon className={cn("size-3.5", busy && "animate-spin")} />
        {failed ? "Try again" : "Refresh"}
      </CoButton>
    </div>
  );
}

/**
 * Why the last read failed, in Zoho's own words.
 *
 * Split from the line above because it belongs in the body of the card rather
 * than beside the count — the message runs to a sentence or two, and squeezing
 * it into the header would push the button off the row.
 */
export function DatasetReadError({ readback }: { readback: ZohoDatasetReadDTO | null | undefined }) {
  if (readback?.status !== "failed" || readback.error === null) return null;

  return (
    <p className="text-xs text-amber-700 dark:text-amber-400">
      Zoho said: {readback.error}
    </p>
  );
}
